import React, {useCallback, useEffect, useState} from 'react';
import { FormattedMessage } from 'react-intl';
import * as PropTypes from 'prop-types';
import {
  Alert, Row, Spin, Button,
} from 'antd';
import Title from 'antd/lib/typography/Title';
import { useSelector, useDispatch } from 'react-redux';
import './Dashboard.css';
import DashboardButtons from './components/DashboardButtons';
import { selectSessionUser } from '../../../state/ui/application/sessionUiReducer';
import DashboardSummaries from './components/DashboardSummaries';
import {
  requestDashboardIssueData,
  requestDashboardPageData,
  requestDashboardRateData, requestDashboardReasonData,
  updateCurrentWidgets
} from '../../../state/ui/application/dashboardUiActions';
import {
  selectCurrentWidgets, selectDashboardIssueDataRequest,
  selectDashboardPageDataRequest,
  selectDashboardRateDataRequest, selectDashboardReasonDataRequest
} from '../../../state/ui/application/dashboardUiReducer';
import { selectDashboardData } from '../../../state/domain/dashboard/dashboardDataReducer';
import { requestCurrentSessionUser } from '../../../state/ui/application/sessionUiActions';
import {getDefaultLocale} from "../../../configWeb";
import {addDaysToJSDate, getDateComparisonNumber} from "../bynx/packageUtil";


// Date options
const dateDisplayOptions = {
  day: 'numeric',
  weekday: 'short',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
};


// Alerts
function AlertsPane({
  isLoading, style, notifications, sessionUser,
}) {
  // Locale adjust
  const userLocale = sessionUser && sessionUser.locale ? sessionUser.locale.replace('_', '-') : getDefaultLocale().locale;

  return (
    <Spin spinning={isLoading}>
      <Row align="top" justify="start" type="flex" style={style}>
        { notifications.map(notification => (
          <Alert
            style={{ width: '100%' }}
            showIcon
            type={notification.notificationType}
            key={notification.notificationId}
            message={(
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div>{notification.message}</div>
                <div style={{
                  flex: '1 0 auto', fontSize: '0.8em', fontStyle: 'italic', color: 'rgb(83, 83, 83)', textAlign: 'end', alignSelf: 'flex-end',
                }}
                >
                  <FormattedMessage id="postedAt" />
                  &nbsp;
                  {new Date(notification.startDateTime).toLocaleDateString(userLocale, dateDisplayOptions)}
                  &nbsp;
                </div>
              </div>
            )}
          />
        ))}
      </Row>
    </Spin>
  );
}

AlertsPane.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  style: PropTypes.oneOfType([PropTypes.object]),
  notifications: PropTypes.arrayOf(PropTypes.object),
  sessionUser: PropTypes.shape({
    locale: PropTypes.string.isRequired,
  }).isRequired,
};

AlertsPane.defaultProps = {
  style: {},
  notifications: [],
};


/**
 * The main dashboard component for the application.
 * @param {object} props The react props for the Dashboard.
 */
function Dashboard({
  sessionUser, loadPageData, isLoading, loadRateData, loadReasonData, loadIssueData,
  isRateDataLoading, isReasonDataLoading, isIssueDataLoading, dashboardData, refreshSessionUser,
  widgetSettings, updateWidgetNumbers, initialFromQueryDate, initialToQueryDate
}) {
  const { displayName } = sessionUser;

  return (
    <div className="via-dashboard-layout via-centre">
      <div className="dashboard-notifications">
        <AlertsPane
          notifications={dashboardData && dashboardData.displayNotifications ? dashboardData.displayNotifications : []}
          isLoading={isLoading}
          style={{ marginBottom: '1em' }}
          sessionUser={sessionUser}
        />
      </div>
      <header className="dashboard-heading">
        <Title level={2} style={{ margin: 0 }}>
          <FormattedMessage id="greeting" />
          { displayName === null ? '' : ', ' }
          <span style={{ display: 'inline-block', wordBreak: 'break-word' }}>{displayName || ''}</span>
        </Title>
        <Button
          icon="reload"
          loading={isLoading}
          onClick={() => {
            const rateOption = widgetSettings && widgetSettings.length > 0 ? widgetSettings[2] : 7;
            const reasonOption = widgetSettings && widgetSettings.length > 0 ? widgetSettings[3] : 7;
            const issueOption = widgetSettings && widgetSettings.length > 0 ? widgetSettings[4] : 7;
            loadPageData(initialFromQueryDate, initialToQueryDate, rateOption, reasonOption, issueOption);
            refreshSessionUser();
          }}
        >
          <FormattedMessage id="refreshData" />
        </Button>
      </header>
      <section className="dashboard-summaries">
        <DashboardSummaries
          dashboardData={dashboardData}
          isLoading={isLoading}
          isRateDataLoading={isRateDataLoading}
          isReasonDataLoading={isReasonDataLoading}
          isIssueDataLoading={isIssueDataLoading}
          loadRateData={loadRateData}
          loadReasonData={loadReasonData}
          loadIssueData={loadIssueData}
          sessionUser={sessionUser}
          widgetSettings={widgetSettings}
          updateWidgetNumbers={updateWidgetNumbers}
        />
      </section>
      <nav className="dashboard-shortcuts">
        <DashboardButtons isLoading={isLoading} />
      </nav>
    </div>
  );
}

Dashboard.propTypes = {
  dashboardData: PropTypes.oneOfType([PropTypes.object]),
  isLoading: PropTypes.bool,
  isRateDataLoading: PropTypes.bool,
  isReasonDataLoading: PropTypes.bool,
  isIssueDataLoading: PropTypes.bool,
  loadPageData: PropTypes.func.isRequired,
  loadRateData: PropTypes.func.isRequired,
  loadReasonData: PropTypes.func.isRequired,
  loadIssueData: PropTypes.func.isRequired,
  sessionUser: PropTypes.shape({
    displayName: PropTypes.string.isRequired,
  }).isRequired,
  refreshSessionUser: PropTypes.func.isRequired,
  initialFromQueryDate: PropTypes.number.isRequired,
  initialToQueryDate: PropTypes.number.isRequired,
  widgetSettings: PropTypes.arrayOf(PropTypes.number),
  updateWidgetNumbers: PropTypes.func,
};

Dashboard.defaultProps = {
  dashboardData: null,
  isLoading: false,
  widgetSettings: [1, 1],
  updateWidgetNumbers: () => {},
};


/**
 * Renders the Dashboard page.
 * Loads all the neccessary data and passes it to the presentation component.
 */
export default function DashboardPage() {
  // Query Dates
  const [initialFromQueryDate, ] = useState(getDateComparisonNumber(addDaysToJSDate(new Date(), -6)));  //-6 as today included in current week figures
  const [initialToQueryDate, ] = useState(getDateComparisonNumber(new Date()));

  // Redux State
  const pageDataRequest = useSelector(selectDashboardPageDataRequest);
  const isLoading = pageDataRequest ? pageDataRequest.isLoading : false;
  const rateDataRequest = useSelector(selectDashboardRateDataRequest);
  const isRateDataLoading = rateDataRequest ? rateDataRequest.isLoading : false;
  const reasonDataRequest = useSelector(selectDashboardReasonDataRequest);
  const isReasonDataLoading = reasonDataRequest ? reasonDataRequest.isLoading : false;
  const issueDataRequest = useSelector(selectDashboardIssueDataRequest);
  const isIssueDataLoading = issueDataRequest ? issueDataRequest.isLoading : false;

  const error = pageDataRequest && pageDataRequest.error;
  const sessionUser = useSelector(selectSessionUser);
  const dashboardData = useSelector(selectDashboardData);
  const widgetSettings = useSelector(selectCurrentWidgets);

  // Keep current options for initial reload if come back to page during session
  const [rateWidgetOption, ] = useState((widgetSettings && widgetSettings.length > 1) ? widgetSettings[2] : 7);
  const [reasonWidgetOption, ] = useState((widgetSettings && widgetSettings.length > 1) ? widgetSettings[3] : 7);
  const [issueWidgetOption, ] = useState((widgetSettings && widgetSettings.length > 1) ? widgetSettings[4] : 7);

  // Redux Actions
  const dispatch = useDispatch();
  const loadPageData = useCallback((fromQDInitial, toQDInitial, rateOpt, reasonOpt, issueOpt) => dispatch(requestDashboardPageData(fromQDInitial, toQDInitial, rateOpt, reasonOpt, issueOpt)), [dispatch]);
  const loadRateData = useCallback((fromQDRate, toQDRate) => dispatch(requestDashboardRateData(fromQDRate, toQDRate)), [dispatch]);
  const loadReasonData = useCallback((fromQDReason, toQDReason) => dispatch(requestDashboardReasonData(fromQDReason, toQDReason)), [dispatch]);
  const loadIssueData = useCallback((fromQDIssue, toQDIssue) => dispatch(requestDashboardIssueData(fromQDIssue, toQDIssue)), [dispatch]);
  const updateWidgetNumbers = useCallback((left, right, option1, option2, option3) => (
    dispatch(updateCurrentWidgets(left, right, option1, option2, option3))
  ), [dispatch]);
  const refreshSessionUser = useCallback(() => dispatch(requestCurrentSessionUser()), [dispatch]);

  // Load all the data on first entry
  useEffect(() => {
    loadPageData(initialFromQueryDate, initialToQueryDate, rateWidgetOption, reasonWidgetOption, issueWidgetOption);
  }, [loadPageData, initialFromQueryDate, initialToQueryDate, rateWidgetOption, reasonWidgetOption, issueWidgetOption]);

  return (
    <Dashboard
      isLoading={isLoading}
      isRateDataLoading={isRateDataLoading}
      isReasonDataLoading={isReasonDataLoading}
      isIssueDataLoading={isIssueDataLoading}
      error={error}
      dashboardData={dashboardData}
      sessionUser={sessionUser}
      widgetSettings={widgetSettings}
      loadPageData={loadPageData}
      loadRateData={loadRateData}
      loadReasonData={loadReasonData}
      loadIssueData={loadIssueData}
      initialFromQueryDate={initialFromQueryDate}
      initialToQueryDate={initialToQueryDate}
      updateWidgetNumbers={updateWidgetNumbers}
      refreshSessionUser={refreshSessionUser}
    />
  );
}
